import React, { type FunctionComponent, useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { QueryParamProvider } from 'use-query-params'
import { Route } from 'react-router'
import { type IPP_SUPPORTED_LOCALE } from '@retailer-platform/shared-components/src/utils/intl/intl.types'
import { makeIntlFormats } from '../../../intl/intl.utils'
import { type DashIntlShape } from '../../../intl/intl.types'
import { trackEvent } from '../../../utils/events/trackEvent'
import { useTrackPageView } from '../../utils/instrumentation/useTrackPageView'
import { useQueryStaleWhileRevalidateCleanup } from '../../../utils/apollo/makeQueryStaleWhileRevalidate'

/**
 * Import function for AppUIProviders chunk.
 * Exported so it can be called early for prefetching while still using React.lazy for rendering.
 */
export const importAppUIProviders = () => import('./components/AppUIProviders')

const AppUIProviders = React.lazy(importAppUIProviders)

interface Props {
  messages: DashIntlShape['messages']
  locale: IPP_SUPPORTED_LOCALE
}

/**
 * Isolated analytics tracker - renders as sibling so URL changes
 * don't cause the main app tree to re-render.
 */
const PageViewTracker = (): null => {
  useTrackPageView()
  return null
}

/**
 * AppProvider wraps the main UI with IntlProvider and QueryParamProvider.
 *
 * Note: ApolloProvider, QueryClientProvider, and AppProviderPreloader have been
 * moved to RPPCore.startWithPendingDomains() for earlier initialization.
 * By the time this component renders, account details AND domains are ready.
 */
export const AppProvider: FunctionComponent<Props> = ({ messages, locale }) => {
  useEffect(() => {
    trackEvent({ id: 'app.launched', description: 'The application was launched' })
  }, [])

  // Runs on mount: prunes stale/wrong-version SWR entries and, when the surviving
  // set still exceeds the namespace budget, evicts oldest-first. Covers read-only
  // sessions where no SWR write fires to trigger the write-path budget eviction,
  // so a prior session's bloat can't linger and starve session/auth writes.
  useQueryStaleWhileRevalidateCleanup()

  return (
    <>
      <PageViewTracker />
      {/* @ts-expect-error TS2769 — strictNullChecks. TODO: fix strict null check */}
      <IntlProvider formats={makeIntlFormats()} locale={locale} messages={messages}>
        <QueryParamProvider ReactRouterRoute={Route}>
          {/* HARD LINE - by this point account AND domains are ready */}
          <React.Suspense fallback={null}>
            <AppUIProviders locale={locale} />
          </React.Suspense>
        </QueryParamProvider>
      </IntlProvider>
    </>
  )
}
